import { routeInspectionsManager } from "@/setup/routes/inspection.routes";
import { pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
	useUserContext,
} from "@dexteel/mesf-core";
import { Grid2, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { InspectionsTable } from "./components/InspectionsTable";
import { Inspection } from "../models/Inspection";
import { getInspections } from "../repositories/InspectionRepository";

type UseInspectionsByShiftProps = {
	shiftId: number | undefined;
	onError: (error: Error) => void;
};

export const useInspectionsByShift = ({
	shiftId,
	onError,
}: UseInspectionsByShiftProps) =>
	useQuery<Inspection[], Error>({
		queryKey: ["inspections", shiftId],
		queryFn: ({ signal }) => getInspections({ shiftId }, signal),
		enabled: !!shiftId,
		onError,
	});

export const InspectionsManagerPage = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	const [error, setError] = useState("");

	const assetId = Number(defaultAreaId);
	const shiftId = shiftInfo?.CurrentShiftId;

	const { data: inspections, isLoading } = useInspectionsByShift({
		shiftId,
		onError: (e) => setError(e.message),
	});

	useEffect(() => {
		if (shiftId) navigate(`${routeInspectionsManager}/${shiftId}`);
	}, [shiftId]);

	useEffect(() => {
		if (!assetId) return;
		(async () => {
			await getShiftDataFromAPI(null, Number(shiftIdParam) || 0, assetId);
		})();
	}, [assetId]);

	return (
		<>
			<HelmetDexteel title="Inspections Manager" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{ backgroundColor: pageBgColor, minHeight: "calc(100vh - 160px)" }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Inspections Manager
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Grid2
						container
						justifyContent="flex-start"
						alignItems="center"
						spacing={2}
					>
						<Grid2 maxWidth="fit-content">
							<ShiftDayNavigatorControl preventInit assetId={assetId} />
						</Grid2>
					</Grid2>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<InspectionsTable
						inspections={inspections ?? []}
						isLoading={isLoading}
						shiftId={shiftId ?? null}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
